import { PropTypes } from 'prop-types';
import { useForm, FormProvider as ReactHookFormProvider } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import { useIntl } from '@zenbusiness/application-commons-intl';
import { captureException } from '@zenbusiness/application-commons-logging';

import Form from '#client/components/Form';
import ShouldLoginDialog from '#client/components/ShouldLoginDialog';
import useShouldLoginDialog from '#client/components/ShouldLoginDialog/hooks/useShouldLoginDialog';
import useFlowState from '#client/hooks/flow/useFlowState';
import useShouldPromptLogin from '#client/hooks/useShouldPromptLogin';
import useUpdateCart from '#client/hooks/useUpdateCart';
import ContinueButton from '#client/pages/Checkout/components/ContinueButton';
import { getBusinessNameWithIndicator } from '#client/utils/businessInfo';
import { formErrorsToTrackingErrorsMapper } from '#client/utils/formErrorsToTrackingErrorsMapper';

function FormProvider({ children, onContinue }) {
  const { authenticated } = useAuthentication();
  const { businessInfo, contactInfo, set } = useFlowState();
  const { formatMessage } = useIntl();
  const shouldPromptLogin = useShouldPromptLogin();
  const { flowPage } = useParams();
  const { handleLogin, isShouldLoginDialogOpen, openShouldLoginDialog, closeShouldLoginDialog } =
    useShouldLoginDialog();

  const isContactInfoRequired = !authenticated;

  const formMethods = useForm({
    defaultValues: {
      ...businessInfo,
      ...(isContactInfoRequired && contactInfo)
    }
  });

  const trackFormSubmitted = useClickstreamCallback('formSubmitted');
  const trackFormSubmissionFailed = useClickstreamCallback('formSubmissionFailed');

  const submitErrorHandler = (_errors) => {
    trackFormSubmissionFailed({ formErrors: formErrorsToTrackingErrorsMapper(_errors) });
  };

  const updateCart = useUpdateCart();

  const submitHandler = async (data) => {
    const shouldLogin = await shouldPromptLogin(data.email);

    if (shouldLogin) {
      openShouldLoginDialog();
      return;
    }

    const formData = {
      businessInfo: {
        formationCounty: data.formationCounty,
        jurisdictionState: data.jurisdictionState,
        name: data.name
      },
      contactInfo: {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
        pageSource: flowPage
      }
    };

    set('businessInfo', (currentData) => ({
      ...currentData,
      ...formData.businessInfo
    }));
    set('contactInfo', (currentData) => ({
      ...currentData,
      ...formData.contactInfo
    }));

    // Do not call updateCart if the business entity is already persisted.
    if (!businessInfo.businessEntityUuid) {
      await updateCart({
        ...formData.businessInfo,
        name: getBusinessNameWithIndicator(data.name, data.companyIndicator)
      });
    }

    onContinue();
  };

  const handleFormSubmit = (e) => {
    trackFormSubmitted({ elementName: 'checkout_business_info_form' });
    formMethods.handleSubmit(submitHandler, submitErrorHandler)(e).catch(captureException);
  };

  const { isSubmitting, errors } = formMethods.formState;
  const hasErrors = Object.keys(errors).length > 0;

  return (
    <ReactHookFormProvider {...formMethods}>
      <Form
        aria-label={formatMessage({ id: 'shopping.page.checkout.business.form.ariaLabel' })}
        data-testid="business-checkout-card-form"
        spacing={2.5}
        onSubmit={handleFormSubmit}
      >
        {children}
        <ContinueButton disabled={hasErrors} loading={isSubmitting} type="submit" />
      </Form>

      <ShouldLoginDialog
        open={isShouldLoginDialogOpen}
        onClose={closeShouldLoginDialog}
        onLogin={handleLogin}
      />
    </ReactHookFormProvider>
  );
}

FormProvider.propTypes = {
  children: PropTypes.node,
  onContinue: PropTypes.func.isRequired
};

export default FormProvider;
